/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.19.0
 */
import { pageApi } from '@/main/api/page.api';
import { userNotificationsTags } from '@/main/modules/app/apiTags';
import { EHttpMethod } from '@/main/modules/app/appConstants';
import { ECreateInquiryType } from '@/main/modules/inquiry/constants';
import { invalidateOnSuccess } from '@/main/utils/invalidateOnSuccess';

import type {
    IInquiryAnswerQuery,
    IInquiryCloseQuery,
    IInquiryFileUploadQuery,
    IInquiryInfoNewFile,
    IInquiryInfoQuery,
} from './types';
import type { IInquiryInfoGeneral } from './types/inquiryInfoType';

export const inquiryInfoApi = pageApi.injectEndpoints({
    endpoints: (builder) => ({
        getInquiryInfoGeneral: builder.query<IInquiryInfoGeneral, IInquiryInfoQuery>({
            query: ({ inquiryId }) => `/inquiry/${inquiryId}`,
        }),

        postInquiryClose: builder.mutation<void, IInquiryCloseQuery>({
            query: (arg) => ({
                url: `/inquiry/${arg.inquiryId}/close`,
                method: EHttpMethod.POST,
                body: arg.note,
            }),
        }),

        postInquiryAnswer: builder.query<void, IInquiryAnswerQuery>({
            query: (arg) => ({
                url: `/inquiry/${arg.inquiryId}/${arg.url}`,
                method: EHttpMethod.POST,
                body: arg.body,
            }),
        }),

        postInquiryAttachFile: builder.mutation<{ fileId: IInquiryInfoNewFile['fileId'] }, IInquiryFileUploadQuery>({
            query: (arg) => {
                const data = new FormData();
                data.append('file', arg.file);
                return {
                    url: `inquiry/attachment/${ECreateInquiryType.card}`,
                    method: EHttpMethod.POST,
                    headers: {
                        'accept': 'application/json;charset=UTF-8',
                        'X-NODE': 'sticky',
                    },
                    body: data,
                };
            },
        }),

        deleteInquiryDetachFile: builder.mutation<void, IInquiryInfoNewFile['fileId']>({
            query: (fileId: IInquiryInfoNewFile['fileId']) => ({
                url: `/inquiry/attachment/${fileId}`,
                method: EHttpMethod.DELETE,
            }),
        }),

        sendInquiryViewed: builder.mutation<void, IInquiryInfoQuery>({
            query: ({ inquiryId }) => ({
                url: `/inquiry/${inquiryId}/viewed`,
                method: EHttpMethod.PUT,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [userNotificationsTags.USER_NOTIFICATIONS_LIST]),
        }),
    }),
});

export const {
    useDeleteInquiryDetachFileMutation,
    useGetInquiryInfoGeneralQuery,
    useLazyGetInquiryInfoGeneralQuery,
    useLazyPostInquiryAnswerQuery,
    usePostInquiryAttachFileMutation,
    usePostInquiryCloseMutation,
    useSendInquiryViewedMutation,
} = inquiryInfoApi;
